/**
 * The prototype's bottom-centre toast.
 *
 * Confirmations stay 2.6s (longer for long text); anything that reads as a failure stays ~10s,
 * in red (`lib/toastDuration.ts` — Akhil, 2026-09-17: the error vanished before it could be
 * read). Hovering holds it, a click dismisses it early.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { isFailure, toastDuration } from '../lib/toastDuration'
import { pushToast, type QueuedToast } from '../lib/toastStack'

const ToastContext = createContext<((message: string) => void) | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  /*
    A STACK, not a slot (Akhil, 2026-09-17: "the toaster is not even staying for 15 secs"). One
    slot meant the next message — a refetch's confirmation, a follow-up write — replaced an error
    within a second of it appearing. Each message now keeps its own timer; a failure sits there
    for its 10s whatever else arrives. Newest at the bottom. A new confirmation replaces the older
    confirmations, though (2026-11-03) — see `lib/toastStack.ts`.
  */
  const [items, setItems] = useState<QueuedToast[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => setItems((list) => list.filter((t) => t.id !== id)), [])
  const toast = useCallback((text: string) => {
    const id = (nextId.current += 1)
    setItems((list) => pushToast(list, { id, text }))
  }, [])
  const value = useMemo(() => toast, [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {items.length > 0 ? (
        <div id="toasts">
          {items.map((t) => (
            <ToastItem key={t.id} text={t.text} onDone={() => dismiss(t.id)} />
          ))}
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}

function ToastItem({ text, onDone }: { text: string; onDone: () => void }) {
  const [held, setHeld] = useState(false)
  useEffect(() => {
    if (held) return
    const timer = setTimeout(onDone, toastDuration(text))
    return () => clearTimeout(timer)
  }, [held, text, onDone])
  const failure = isFailure(text)
  return (
    <div
      className="toast"
      role={failure ? 'alert' : 'status'}
      title="Click to dismiss"
      style={failure ? { background: 'var(--danger)', color: '#fff' } : undefined}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onClick={onDone}
    >
      {text}
    </div>
  )
}

export function useToast(): (message: string) => void {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
